import { useState, useEffect, useCallback } from 'react';
import API from '../api';
import ModalNominaJpg from '../components/ModalNominaJpg';

const fmt = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);

const MESES_LABEL = ['ENERO','FEBRERO','MARZO','ABRIL','MAYO','JUNIO','JULIO','AGOSTO','SEPTIEMBRE','OCTUBRE','NOVIEMBRE','DICIEMBRE'];
const CATEGORIAS  = ['HOGAR','NOMINA','TRABAJO','BICICLETA','PERSONAL'];
const _hoyGastos = new Date();
const ANIO_ACTUAL_GASTOS = String(_hoyGastos.getFullYear());
const MES_ACTUAL_GASTOS = MESES_LABEL[_hoyGastos.getMonth()];
const ANIOS_GASTOS = (() => { const list = []; for (let a = _hoyGastos.getFullYear(); a >= 2025; a--) list.push(String(a)); return list; })();
const TIPOS_GASTO = [
  { key: 'TODOS', label: 'Todos' },
  { key: 'FIJO', label: 'Fijos' },
  { key: 'VARIABLE', label: 'Variables' },
  { key: 'NOMINA', label: 'Nómina' },
];

const CAT_COLOR = {
  HOGAR:     { bg: 'rgba(99,102,241,0.08)',  color: '#4f46e5'  },
  NOMINA:    { bg: 'rgba(5,150,105,0.08)',   color: '#059669'  },
  TRABAJO:   { bg: 'rgba(0,184,227,0.08)',   color: '#0087a8'  },
  BICICLETA: { bg: 'rgba(251,191,36,0.08)', color: '#b45309'  },
  PERSONAL:  { bg: 'rgba(248,113,113,0.08)', color: '#dc2626'  },
  OTRO:      { bg: 'rgba(90,122,138,0.08)', color: '#5a7a8a'  },
};

const inputStyle = { background: '#f4f7fa', border: '1px solid #e8eef2', color: '#08293f' };

function nowPeriodo() {
  const h = new Date();
  return h.getFullYear() + '-' + String(h.getMonth() + 1).padStart(2, '0');
}
function mesLabel(periodo) {
  const [y, m] = periodo.split('-');
  return MESES_LABEL[parseInt(m) - 1] + ' ' + y;
}

// ── Tag de categoría ─────────────────────────────────────────────
function CatTag({ cat }) {
  const s = CAT_COLOR[cat] || CAT_COLOR.OTRO;
  return (
    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', padding: '2px 7px',
      background: s.bg, color: s.color, borderRadius: 2 }}>{cat}</span>
  );
}

function TipoTag({ tipo }) {
  const label = tipo === 'FIJO' ? 'FIJO' : tipo === 'NOMINA' ? 'NOMINA' : 'VARIABLE';
  const s = {
    FIJO: { bg: 'rgba(0,184,227,0.08)', color: '#0087a8' },
    VARIABLE: { bg: 'rgba(251,191,36,0.08)', color: '#b45309' },
    NOMINA: { bg: 'rgba(5,150,105,0.08)', color: '#059669' },
  }[label];
  return (
    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', padding: '2px 7px',
      background: s.bg, color: s.color, borderRadius: 2 }}>{label}</span>
  );
}

// ════════════════════════════════════════════════════════════════
// TAB 1 — Catálogo de gastos fijos
// ════════════════════════════════════════════════════════════════
// eslint-disable-next-line no-unused-vars
function TabCatalogo() {
  const [items,    setItems]    = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId,   setEditId]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [msg,      setMsg]      = useState({ text: '', ok: true });
  const [periodo,  setPeriodo]  = useState(nowPeriodo());
  const emptyF = { nombre: '', categoria: 'TRABAJO', valor: '', proveedor: '' };
  const [form, setForm] = useState(emptyF);

  const cargar = useCallback(() => {
    const mes = encodeURIComponent(mesLabel(periodo));
    API.get(`/gastos-fijos?mes=${mes}`).then(r => setItems(r.data));
  }, [periodo]);
  useEffect(() => { cargar(); }, [cargar]);

  // periodos desde enero 2025
  const periodos = [];
  const _h = new Date();
  let _py = _h.getFullYear(), _pm = _h.getMonth();
  while (_py > 2025 || (_py === 2025 && _pm >= 0)) {
    periodos.push(_py + '-' + String(_pm + 1).padStart(2, '0'));
    _pm--; if (_pm < 0) { _pm = 11; _py--; }
    if (_py < 2025) break;
  }

  const showMsg = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg({ text: '', ok: true }), 4000); };

  async function eliminarItem(id) {
    if (!window.confirm('¿Eliminar este gasto fijo del catálogo?')) return;
    await API.delete(`/gastos-fijos/${id}`); cargar();
  }

  async function cargarItem(id) {
    try {
      await API.post(`/gastos-fijos/${id}/cargar`, { mes: mesLabel(periodo) });
      showMsg(`✓ Cargado a ${mesLabel(periodo)}`);
      cargar();
    } catch (err) {
      const e = err.response?.data?.error;
      showMsg(e === 'YA_CARGADO' ? `⚠ Ya fue cargado en ${mesLabel(periodo)}` : 'Error: ' + e, false);
    }
  }

  async function guardar(e) {
    e.preventDefault(); setLoading(true);
    try {
      if (editId) await API.put(`/gastos-fijos/${editId}`, form);
      else        await API.post('/gastos-fijos', form);
      setShowForm(false); setEditId(null); setForm(emptyF); cargar();
    } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  }

  function editar(item) {
    setForm({ nombre: item.nombre, categoria: item.categoria, valor: item.valor, proveedor: item.proveedor });
    setEditId(item.id); setShowForm(true);
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <p style={{ fontSize: 11, color: '#5a7a8a', margin: 0 }}>Items recurrentes</p>
          <select value={periodo} onChange={e => setPeriodo(e.target.value)} className="input" style={{ ...inputStyle, width: 'auto', fontSize: 11, padding: '4px 8px' }}>
            {periodos.map(p => <option key={p} value={p}>{mesLabel(p)}</option>)}
          </select>
        </div>
        <button className="btn-primary" onClick={() => { setForm(emptyF); setEditId(null); setShowForm(true); }}>+ Agregar</button>
      </div>

      {msg.text && (
        <div style={{ marginBottom: 12, padding: '9px 14px', fontSize: 12, fontWeight: 600, borderRadius: 2,
          background: msg.ok ? 'rgba(0,184,227,0.08)' : 'rgba(251,191,36,0.1)',
          border: `1px solid ${msg.ok ? 'rgba(0,184,227,0.25)' : 'rgba(251,191,36,0.3)'}`,
          color: msg.ok ? '#00b8e3' : '#b45309' }}>{msg.text}</div>
      )}

      {/* Lista catálogo */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(item => (
          <div key={item.id} style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '12px 16px',
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 150 }}>
              <div style={{ fontWeight: 700, color: '#08293f', fontSize: 13 }}>{item.nombre}</div>
              <div style={{ fontSize: 11, color: '#5a7a8a', marginTop: 2 }}>{item.proveedor || '—'}</div>
            </div>
            <CatTag cat={item.categoria} />
            <div style={{ fontWeight: 700, color: '#08293f', fontSize: 15, minWidth: 100, textAlign: 'right' }}>{fmt(item.valor)}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => editar(item)} style={{ fontSize: 11, color: '#00b8e3', background: 'none', border: 'none', cursor: 'pointer' }}>Editar</button>
              <button onClick={() => eliminarItem(item.id)} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Eliminar</button>
              <button
                onClick={() => cargarItem(item.id)}
                disabled={item.cargado_mes}
                style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', cursor: item.cargado_mes ? 'default' : 'pointer',
                  background: item.cargado_mes ? '#f4f7fa' : '#08293f',
                  color: item.cargado_mes ? '#b3b3b3' : '#00b8e3',
                  border: `1px solid ${item.cargado_mes ? '#e8eef2' : 'rgba(0,184,227,0.3)'}`,
                  borderRadius: 2 }}>
                {item.cargado_mes ? '✓ Cargado' : '⚡ Cargar mes'}
              </button>
            </div>
          </div>
        ))}
        {!items.length && (
          <div style={{ textAlign: 'center', padding: '30px 0', color: '#b3b3b3', fontSize: 12 }}>
            No hay items en el catálogo. Agrega uno para comenzar.
          </div>
        )}
      </div>

      {/* Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 440 }}>
            <div style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontSize: 13, fontWeight: 700, color: '#08293f', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {editId ? 'Editar gasto fijo' : 'Nuevo gasto fijo'}
                  </h2>
                  <div style={{ width: 28, height: 2, background: '#00b8e3', marginTop: 5 }} />
                </div>
                <button onClick={() => setShowForm(false)} style={{ fontSize: 20, color: '#b3b3b3', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
              </div>
              <form onSubmit={guardar}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label className="label">Nombre *</label>
                    <input className="input" value={form.nombre} style={inputStyle} required
                      onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">Categoría</label>
                    <select className="input" value={form.categoria} style={inputStyle}
                      onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}>
                      {CATEGORIAS.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">Valor</label>
                    <input className="input" type="number" value={form.valor} style={inputStyle} required min="0"
                      onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} />
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label className="label">Proveedor</label>
                    <input className="input" value={form.proveedor} style={inputStyle}
                      onChange={e => setForm(f => ({ ...f, proveedor: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" className="btn-primary" disabled={loading} style={{ flex: 1, padding: 9 }}>
                    {loading ? 'Guardando...' : editId ? 'Actualizar' : 'Guardar'}
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// TAB 2 — GastOS
// ════════════════════════════════════════════════════════════════
function TabGastosMes() {
  const [gastos,   setGastos]   = useState([]);
  const [fijosCatalogo, setFijosCatalogo] = useState([]);
  const [filtros,  setFiltros]  = useState({ anio: ANIO_ACTUAL_GASTOS, mes: MES_ACTUAL_GASTOS });
  const [filtroTipo, setFiltroTipo] = useState('TODOS');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [showForm, setShowForm] = useState(false);
  const [editId,   setEditId]   = useState(null);
  const [editFijoId, setEditFijoId] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const emptyF = { fecha: new Date().toISOString().slice(0,10), descripcion: '', categoria: 'PERSONAL', tipo: 'VARIABLE', valor: '', proveedor: '', iva_porcentaje: '0' };
  const [form, setForm] = useState(emptyF);

  const cargar = useCallback(() => {
    const params = {};
    if (filtros.anio && filtros.mes) params.mes = `${filtros.mes} ${filtros.anio}`;
    else if (filtros.anio) params.anio = filtros.anio;
    else if (filtros.mes) params.mes_nombre = filtros.mes;
    const mesExacto = filtros.anio && filtros.mes ? `${filtros.mes} ${filtros.anio}` : null;
    Promise.all([
      API.get('/gastos', { params }),
      mesExacto ? API.get(`/gastos-fijos?mes=${encodeURIComponent(mesExacto)}`) : Promise.resolve({ data: [] }),
    ]).then(([g, f]) => {
      setGastos(g.data);
      setFijosCatalogo(Array.isArray(f.data) ? f.data : []);
    });
  }, [filtros]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { setSeleccionados(new Set()); }, [filtros, filtroTipo, filtroCategoria]);

  const fijosPendientes = fijosCatalogo
    .filter(f => !f.cargado_mes)
    .map(f => ({
      id: `fijo-${f.id}`,
      fijo_id: f.id,
      fecha: null,
      descripcion: f.nombre,
      categoria: f.categoria || 'TRABAJO',
      tipo: 'FIJO',
      proveedor: f.proveedor || '',
      valor: f.valor,
      iva_valor: 0,
      pendiente_fijo: true,
    }));
  const gastosVista = [...gastos, ...fijosPendientes];
  const gastosFiltrados = gastosVista.filter(g => {
    const tipoOk = filtroTipo === 'TODOS' || (g.tipo || 'VARIABLE') === filtroTipo;
    const catOk = !filtroCategoria || (g.categoria || 'OTRO') === filtroCategoria;
    return tipoOk && catOk;
  });
  const total      = gastosFiltrados.reduce((s, g) => s + parseFloat(g.valor || 0), 0);
  const totalTipo  = tipo => gastosFiltrados.filter(g => (g.tipo || 'VARIABLE') === tipo).reduce((s, g) => s + parseFloat(g.valor || 0), 0);
  const periodoLabel = filtros.anio && filtros.mes ? `${filtros.mes} ${filtros.anio}`
    : filtros.anio ? `AÑO ${filtros.anio}`
    : filtros.mes ? `${filtros.mes} · TODOS LOS AÑOS`
    : 'TODOS LOS PERIODOS';
  const filtroLabel = [
    filtroTipo === 'TODOS' ? null : TIPOS_GASTO.find(t => t.key === filtroTipo)?.label,
    filtroCategoria ? filtroCategoria : null,
  ].filter(Boolean).join(' · ');
  const puedeCopiarSiguiente = Boolean(filtros.anio && filtros.mes && seleccionados.size > 0);
  const filasSeleccionables = gastosFiltrados.map(g => g.id);
  const todosSeleccionados = filasSeleccionables.length > 0 && filasSeleccionables.every(id => seleccionados.has(id));

  function toggleSeleccion(id) {
    setSeleccionados(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleTodos() {
    setSeleccionados(prev => {
      const next = new Set(prev);
      if (todosSeleccionados) filasSeleccionables.forEach(id => next.delete(id));
      else filasSeleccionables.forEach(id => next.add(id));
      return next;
    });
  }

  async function guardar(e) {
    e.preventDefault(); setLoading(true);
    try {
      if (editFijoId) {
        if (form.tipo === 'FIJO') {
          await API.put(`/gastos-fijos/${editFijoId}`, {
            nombre: form.descripcion,
            categoria: form.categoria,
            valor: form.valor,
            proveedor: form.proveedor,
            activo: 1,
          });
        } else {
          const mesIdx = MESES_LABEL.indexOf(filtros.mes);
          const fechaMes = filtros.anio && mesIdx >= 0
            ? `${filtros.anio}-${String(mesIdx + 1).padStart(2, '0')}-01`
            : form.fecha;
          await API.post('/gastos', { ...form, fecha: fechaMes });
          await API.delete(`/gastos-fijos/${editFijoId}`);
        }
      } else if (editId) await API.put(`/gastos/${editId}`, form);
      else await API.post('/gastos', form);
      setShowForm(false); setEditId(null); setEditFijoId(null); setForm(emptyF); cargar();
    } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    await API.delete(`/gastos/${id}`); cargar();
  }

  async function eliminarFijo(id) {
    if (!window.confirm('¿Eliminar este gasto fijo del catálogo?')) return;
    await API.delete(`/gastos-fijos/${id}`); cargar();
  }

  async function copiarSeleccionadosSiguiente() {
    if (!puedeCopiarSiguiente) return;
    const rows = gastosFiltrados.filter(g => seleccionados.has(g.id));
    const gastos_ids = rows.filter(g => !g.pendiente_fijo).map(g => g.id);
    const fijos_ids = rows.filter(g => g.pendiente_fijo).map(g => g.fijo_id);
    if (!window.confirm(`¿Copiar ${rows.length} gasto(s) seleccionados al mes siguiente?`)) return;
    try {
      const { data } = await API.post('/gastos/copiar-siguiente', {
        mes: `${filtros.mes} ${filtros.anio}`,
        gastos_ids,
        fijos_ids,
      });
      setSeleccionados(new Set());
      cargar();
      alert(`Copiados a ${data.mes_destino}: ${data.creados}. Omitidos por duplicado: ${data.omitidos}.`);
    } catch (err) {
      const e = err.response?.data?.error;
      alert('Error: ' + (e || err.message));
    }
  }

  function editar(g) {
    if (g.pendiente_fijo) {
      setForm({ fecha: new Date().toISOString().slice(0,10), descripcion: g.descripcion, categoria: g.categoria,
        tipo: 'FIJO', valor: g.valor, proveedor: g.proveedor, iva_porcentaje: '0' });
      setEditId(null); setEditFijoId(g.fijo_id); setShowForm(true);
      return;
    }
    setForm({ fecha: g.fecha?.slice(0,10), descripcion: g.descripcion, categoria: g.categoria,
      tipo: g.tipo || 'VARIABLE', valor: g.valor, proveedor: g.proveedor, iva_porcentaje: String(g.iva_porcentaje || '0') });
    setEditId(g.id); setEditFijoId(null); setShowForm(true);
  }

  return (
    <div>
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 10 }}>
          — Resumen de gastos · {periodoLabel}{filtroLabel ? ` · ${filtroLabel}` : ''}
        </div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#5a7a8a' }}><strong style={{ color: '#08293f' }}>{gastosFiltrados.length}</strong> gastos</span>
          {[
            { label: 'Gasto total', value: total, color: '#00b8e3' },
            { label: 'Nómina', value: totalTipo('NOMINA'), color: '#059669' },
            { label: 'Gastos fijos', value: totalTipo('FIJO'), color: '#0087a8' },
            { label: 'Gastos variables', value: totalTipo('VARIABLE'), color: '#b45309' },
          ].map(k => (
            <div key={k.label}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#5a7a8a', marginBottom: 1 }}>{k.label}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: k.color }}>{fmt(k.value)}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filtros.anio} onChange={e => setFiltros(f => ({ ...f, anio: e.target.value }))} className="input" style={{ ...inputStyle, width: 'auto', fontSize: 12 }}>
            <option value="">Todos los años</option>
            {ANIOS_GASTOS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={filtros.mes} onChange={e => setFiltros(f => ({ ...f, mes: e.target.value }))} className="input" style={{ ...inputStyle, width: 'auto', fontSize: 12 }}>
            <option value="">Todos los meses</option>
            {MESES_LABEL.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)} className="input" style={{ ...inputStyle, width: 'auto', fontSize: 12 }}>
            <option value="">Todas las categorías</option>
            {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 2, background: '#f4f7fa', border: '1px solid #e8eef2', padding: 2, borderRadius: 2 }}>
            {TIPOS_GASTO.map(t => (
              <button key={t.key} onClick={() => setFiltroTipo(t.key)} style={{
            padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', border: 'none', borderRadius: 2,
                background: filtroTipo === t.key ? '#08293f' : 'transparent',
                color: filtroTipo === t.key ? '#00b8e3' : '#5a7a8a',
              }}>{t.label}</button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn-secondary" disabled={!puedeCopiarSiguiente} onClick={copiarSeleccionadosSiguiente} style={{ opacity: puedeCopiarSiguiente ? 1 : 0.45 }}>
            Copiar seleccionados al siguiente mes
          </button>
          <button className="btn-primary" onClick={() => { setForm(emptyF); setEditId(null); setEditFijoId(null); setShowForm(true); }}>+ Gasto puntual</button>
        </div>
      </div>

      {/* Lista */}
      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 34 }}>
                <input type="checkbox" checked={todosSeleccionados} onChange={toggleTodos} />
              </th>
              <th>Fecha</th><th>Descripción</th><th>Tipo</th><th>Categoría</th><th>Proveedor</th>
              <th style={{ textAlign: 'right' }}>Valor</th>
              <th style={{ textAlign: 'right' }}>IVA desc.</th><th></th>
            </tr>
          </thead>
          <tbody>
            {gastosFiltrados.map(g => (
              <tr key={g.id}>
                <td>
                  <input type="checkbox" checked={seleccionados.has(g.id)} onChange={() => toggleSeleccion(g.id)} />
                </td>
                <td style={{ color: '#5a7a8a', fontSize: 12 }}>{g.pendiente_fijo ? 'Pendiente' : g.fecha?.slice(0,10)}</td>
                <td style={{ fontWeight: 600, color: '#08293f' }}>
                  {g.descripcion}
                  {g.pendiente_fijo && <span style={{ marginLeft: 8, fontSize: 9, fontWeight: 700, color: '#b45309', background: 'rgba(251,191,36,0.1)', padding: '2px 6px', borderRadius: 2 }}>POR CARGAR</span>}
                </td>
                <td><TipoTag tipo={g.tipo} /></td>
                <td><CatTag cat={g.categoria} /></td>
                <td style={{ color: '#b3b3b3', fontSize: 12 }}>{g.proveedor}</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: '#08293f' }}>{fmt(g.valor)}</td>
                <td style={{ textAlign: 'right', color: '#059669', fontSize: 12 }}>
                  {parseFloat(g.iva_valor) > 0 ? fmt(g.iva_valor) : '—'}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => editar(g)} style={{ fontSize: 11, color: '#00b8e3', background: 'none', border: 'none', cursor: 'pointer' }}>Editar</button>
                    <button onClick={() => g.pendiente_fijo ? eliminarFijo(g.fijo_id) : eliminar(g.id)} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Borrar</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          {gastosFiltrados.length > 0 && (
            <tfoot>
              <tr style={{ borderTop: '1px solid var(--app-border)', background: '#f4f7fa' }}>
                <td colSpan={6} style={{ padding: '10px 12px', fontWeight: 700, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#08293f' }}>Total</td>
                <td style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 700, color: '#dc2626' }}>{fmt(total)}</td>
                <td />
                <td />
              </tr>
            </tfoot>
          )}
        </table>
        {!gastosFiltrados.length && (
          <div style={{ textAlign: 'center', padding: '30px 0', color: '#b3b3b3', fontSize: 12 }}>
            Sin gastos registrados para {periodoLabel}{filtroLabel ? ` · ${filtroLabel}` : ''}.
          </div>
        )}
      </div>

      {/* Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 440 }}>
            <div style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontSize: 13, fontWeight: 700, color: '#08293f', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {editFijoId ? 'Editar gasto fijo' : editId ? 'Editar gasto' : 'Nuevo gasto puntual'}
                  </h2>
                  <div style={{ width: 28, height: 2, background: '#00b8e3', marginTop: 5 }} />
                </div>
                <button onClick={() => setShowForm(false)} style={{ fontSize: 20, color: '#b3b3b3', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
              </div>
              <form onSubmit={guardar}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label className="label">Fecha *</label>
                    <input className="input" type="date" value={form.fecha} style={inputStyle} required
                      onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">Categoría</label>
                    <select className="input" value={form.categoria} style={inputStyle}
                      onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}>
                      {CATEGORIAS.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">Tipo</label>
                    <select className="input" value={form.tipo} style={inputStyle}
                      onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
                      <option value="NOMINA">Nómina</option>
                      <option value="VARIABLE">Variable</option>
                      <option value="FIJO">Fijo</option>
                    </select>
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label className="label">Descripción *</label>
                    <input className="input" value={form.descripcion} style={inputStyle} required
                      onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">Valor (base sin IVA)</label>
                    <input className="input" type="number" value={form.valor} style={inputStyle} required min="0"
                      onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">Proveedor</label>
                    <input className="input" value={form.proveedor} style={inputStyle}
                      onChange={e => setForm(f => ({ ...f, proveedor: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">IVA</label>
                    <select className="input" value={form.iva_porcentaje} style={inputStyle}
                      onChange={e => setForm(f => ({ ...f, iva_porcentaje: e.target.value }))}>
                      <option value="0">Sin IVA</option>
                      <option value="0.19">19% IVA</option>
                    </select>
                  </div>
                  {parseFloat(form.iva_porcentaje) > 0 && (
                    <div>
                      <label className="label">IVA descontable</label>
                      <input className="input" readOnly
                        value={fmt(Math.round((parseFloat(form.valor) || 0) * parseFloat(form.iva_porcentaje)))}
                        style={{ background: 'rgba(5,150,105,0.06)', border: '1px solid rgba(5,150,105,0.2)', color: '#059669', fontWeight: 700 }} />
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" className="btn-primary" disabled={loading} style={{ flex: 1, padding: 9 }}>
                    {loading ? 'Guardando...' : (editId || editFijoId) ? 'Actualizar' : 'Guardar'}
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// PANEL BENEFICIOS — vacaciones, votos, día de familia
// ════════════════════════════════════════════════════════════════
const anioActual = new Date().getFullYear();
const ANIOS_BENEF = Array.from({ length: anioActual - 2024 }, (_, i) => anioActual - i);

function calcDiasHabiles(fi, ff) {
  if (!fi || !ff) return 0;
  let d = new Date(fi + 'T12:00:00'), fin = new Date(ff + 'T12:00:00'), count = 0;
  while (d <= fin) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

function fmtFecha(f) {
  if (!f) return '—';
  const d = new Date(String(f).slice(0, 10) + 'T12:00:00');
  return d.getDate() + ' ' + ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'][d.getMonth()] + ' ' + d.getFullYear();
}

function BeneficiosPanel({ empleadoId, refreshKey }) {
  const [anio, setAnio]           = useState(anioActual);
  const [data, setData]           = useState(null);
  const [showVacForm, setShowVacForm] = useState(false);
  const [showVotoForm, setShowVotoForm] = useState(false);
  const [familiaFecha, setFamiliaFecha] = useState('');
  const [vacForm, setVacForm]     = useState({ fecha_inicio: '', fecha_fin: '', festivos: 0, notas: '' });
  const [editFest, setEditFest]   = useState({}); // { [vacId]: newValue }
  const [votoForm, setVotoForm]   = useState({ nombre: '', fecha: '' });

  const cargar = useCallback(() => {
    API.get(`/empleados/${empleadoId}/beneficios?anio=${anio}`).then(r => setData(r.data));
  }, [empleadoId, anio, refreshKey]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);

  async function agregarVacacion(e) {
    e.preventDefault();
    const habiles   = calcDiasHabiles(vacForm.fecha_inicio, vacForm.fecha_fin);
    const festivos  = Math.max(0, parseInt(vacForm.festivos) || 0);
    const dias      = Math.max(0, habiles - festivos);
    await API.post(`/empleados/${empleadoId}/vacaciones`, { anio, ...vacForm, dias, festivos });
    setShowVacForm(false); setVacForm({ fecha_inicio: '', fecha_fin: '', festivos: 0, notas: '' }); cargar();
  }

  async function borrarVacacion(id) {
    if (!window.confirm('¿Eliminar este período?')) return;
    await API.delete(`/empleados/${empleadoId}/vacaciones/${id}`); cargar();
  }

  async function guardarFestivos(v) {
    const fest = Math.max(0, parseInt(editFest[v.id]) || 0);
    const habiles = calcDiasHabiles(
      v.fecha_inicio instanceof Date ? v.fecha_inicio.toISOString().slice(0,10) : String(v.fecha_inicio).slice(0,10),
      v.fecha_fin   instanceof Date ? v.fecha_fin.toISOString().slice(0,10)   : String(v.fecha_fin).slice(0,10)
    );
    const dias = Math.max(0, habiles - fest);
    await API.put(`/empleados/${empleadoId}/vacaciones/${v.id}`, { festivos: fest, dias });
    setEditFest(p => { const n = { ...p }; delete n[v.id]; return n; });
    cargar();
  }

  async function agregarVoto(e) {
    e.preventDefault();
    await API.post(`/empleados/${empleadoId}/dias-especiales`, { anio, tipo: 'VOTO', ...votoForm });
    setShowVotoForm(false); setVotoForm({ nombre: '', fecha: '' }); cargar();
  }

  async function borrarDia(id) {
    await API.delete(`/empleados/${empleadoId}/dias-especiales/${id}`); cargar();
  }

  async function guardarFamilia(e) {
    e.preventDefault();
    await API.post(`/empleados/${empleadoId}/dias-especiales`, { anio, tipo: 'FAMILIA', nombre: 'Día de la familia', fecha: familiaFecha || null });
    setFamiliaFecha(''); cargar();
  }

  if (!data) return null;

  const pctVac = data.diasGanados > 0 ? Math.min(Math.round(data.diasUsados / data.diasGanados * 100), 100) : 0;
  const totalVotosDias = (data.votos?.length || 0) * 0.5;

  return (
    <div style={{ marginTop: 16 }}>
      {/* Header beneficios */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a' }}>— Beneficios y días especiales</div>
        <select value={anio} onChange={e => setAnio(Number(e.target.value))} className="input" style={{ ...inputStyle, width: 'auto', fontSize: 12 }}>
          {ANIOS_BENEF.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {/* ── Vacaciones ── */}
      <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '16px 18px', marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#08293f' }}>Vacaciones</div>
          <button onClick={() => setShowVacForm(v => !v)} style={{ fontSize: 11, fontWeight: 600, color: '#00b8e3', background: 'none', border: 'none', cursor: 'pointer' }}>
            {showVacForm ? '× Cancelar' : '+ Registrar período'}
          </button>
        </div>

        {/* Años trabajados + próximo aniversario */}
        {data.fechaIngreso && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, background: '#f4f7fa', padding: '4px 10px', borderRadius: 2, color: '#5a7a8a' }}>
              {data.anosCompletos} año{data.anosCompletos !== 1 ? 's' : ''} trabajado{data.anosCompletos !== 1 ? 's' : ''} · 15 días/año
            </span>
            {data.proxAniversario && (
              <span style={{ fontSize: 11, background: 'rgba(0,184,227,0.06)', padding: '4px 10px', borderRadius: 2, color: '#00b8e3', border: '1px solid rgba(0,184,227,0.2)' }}>
                Próximo aniversario: {fmtFecha(data.proxAniversario)}
              </span>
            )}
          </div>
        )}

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 10 }}>
          {[
            { label: 'Días ganados', value: data.diasGanados, sub: 'total acumulado', color: '#08293f' },
            { label: 'Días usados',  value: data.diasUsadosTotal ?? data.diasUsados, sub: 'histórico total', color: '#b45309' },
            { label: 'Disponibles',  value: data.diasDisponibles, sub: 'por tomar', color: data.diasDisponibles > 0 ? '#059669' : '#dc2626' },
          ].map((k, i) => (
            <div key={i} style={{ background: '#f9fafb', padding: '10px 12px', borderRadius: 2, textAlign: 'center' }}>
              <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 3 }}>{k.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 10, color: '#b3b3b3', marginTop: 1 }}>{k.sub}</div>
            </div>
          ))}
        </div>

        {/* Barra progreso */}
        <div style={{ height: 7, background: '#f4f7fa', borderRadius: 4, overflow: 'hidden', marginBottom: 10 }}>
          <div style={{ width: `${pctVac}%`, height: '100%', background: pctVac >= 100 ? '#dc2626' : '#00b8e3', borderRadius: 4, transition: 'width 0.5s' }} />
        </div>

        {/* Lista períodos */}
        {data.vacaciones.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            {data.vacaciones.map(v => (
              <div key={v.id} style={{ padding: '7px 10px', background: 'rgba(0,184,227,0.04)', borderRadius: 2, border: '1px solid rgba(0,184,227,0.12)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#08293f' }}>{fmtFecha(v.fecha_inicio)} → {fmtFecha(v.fecha_fin)}</span>
                    <span style={{ fontSize: 11, color: '#5a7a8a', marginLeft: 8 }}>{v.dias} días hábiles</span>
                    {(v.festivos > 0 && !(v.id in editFest)) && <span style={{ fontSize: 11, color: '#b45309', marginLeft: 8 }}>· {v.festivos} festivo{v.festivos !== 1 ? 's' : ''}</span>}
                    {v.notas && <span style={{ fontSize: 11, color: '#b3b3b3', marginLeft: 8 }}>· {v.notas}</span>}
                  </div>
                  <button onClick={() => borrarVacacion(v.id)} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Borrar</button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                  <span style={{ fontSize: 11, color: '#5a7a8a' }}>Festivos:</span>
                  <input
                    type="number" min="0" style={{ width: 52, padding: '2px 6px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4, background: '#fff' }}
                    value={v.id in editFest ? editFest[v.id] : (v.festivos || 0)}
                    onChange={e => setEditFest(p => ({ ...p, [v.id]: e.target.value }))}
                  />
                  {v.id in editFest && (
                    <button onClick={() => guardarFestivos(v)} style={{ fontSize: 11, fontWeight: 600, color: '#fff', background: '#00b8e3', border: 'none', borderRadius: 4, padding: '2px 10px', cursor: 'pointer' }}>
                      Guardar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {data.vacaciones.length === 0 && !showVacForm && (
          <div style={{ fontSize: 11, color: '#b3b3b3', textAlign: 'center', padding: '8px 0' }}>Sin vacaciones registradas en {anio}. El saldo de días disponibles refleja todo el historial.</div>
        )}

        {/* Formulario agregar vacación */}
        {showVacForm && (() => {
          const habiles  = calcDiasHabiles(vacForm.fecha_inicio, vacForm.fecha_fin);
          const festivos = Math.max(0, parseInt(vacForm.festivos) || 0);
          const netos    = Math.max(0, habiles - festivos);
          return (
            <form onSubmit={agregarVacacion} style={{ marginTop: 8, padding: '12px', background: '#f9fafb', borderRadius: 2 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
                <div>
                  <label className="label">Fecha inicio *</label>
                  <input className="input" type="date" value={vacForm.fecha_inicio} style={inputStyle} required
                    onChange={e => setVacForm(f => ({ ...f, fecha_inicio: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Fecha fin *</label>
                  <input className="input" type="date" value={vacForm.fecha_fin} style={inputStyle} required
                    onChange={e => setVacForm(f => ({ ...f, fecha_fin: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Festivos en el período</label>
                  <input className="input" type="number" min="0" max={habiles} value={vacForm.festivos} style={inputStyle}
                    onChange={e => setVacForm(f => ({ ...f, festivos: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Notas</label>
                  <input className="input" value={vacForm.notas} style={inputStyle} placeholder="Ej: vacaciones navideñas"
                    onChange={e => setVacForm(f => ({ ...f, notas: e.target.value }))} />
                </div>
                {vacForm.fecha_inicio && vacForm.fecha_fin && (
                  <div style={{ gridColumn: '1/-1', background: 'rgba(0,184,227,0.06)', borderRadius: 2, padding: '10px 12px', border: '1px solid rgba(0,184,227,0.15)' }}>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
                      <span style={{ color: '#5a7a8a' }}>Días hábiles (sin fines de semana): <strong style={{ color: '#08293f' }}>{habiles}</strong></span>
                      {festivos > 0 && <span style={{ color: '#5a7a8a' }}>− Festivos: <strong style={{ color: '#b45309' }}>{festivos}</strong></span>}
                      <span style={{ color: '#5a7a8a' }}>= <strong style={{ color: '#059669', fontSize: 14 }}>{netos} días a descontar</strong></span>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn-primary" style={{ flex: 1, padding: '7px', fontSize: 12 }}>Guardar</button>
                <button type="button" className="btn-secondary" onClick={() => setShowVacForm(false)}>Cancelar</button>
              </div>
            </form>
          );
        })()}
      </div>

      {/* ── Día de voto ── */}
      <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '16px 18px', marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#08293f' }}>Días de voto {anio}</div>
            <div style={{ fontSize: 10, color: '#5a7a8a', marginTop: 2 }}>0.5 días compensatorios por cada elección</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {data.votos.length > 0 && (
              <span style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', background: 'rgba(99,102,241,0.08)', padding: '3px 10px', borderRadius: 2 }}>
                {totalVotosDias} día{totalVotosDias !== 1 ? 's' : ''} compensatorio{totalVotosDias !== 1 ? 's' : ''}
              </span>
            )}
            <button onClick={() => setShowVotoForm(v => !v)} style={{ fontSize: 11, fontWeight: 600, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>
              {showVotoForm ? '× Cancelar' : '+ Agregar elección'}
            </button>
          </div>
        </div>

        {data.votos.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: showVotoForm ? 10 : 0 }}>
            {data.votos.map(v => (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', background: 'rgba(99,102,241,0.04)', borderRadius: 2, border: '1px solid rgba(99,102,241,0.12)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18, lineHeight: 1 }}>🗳</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#08293f' }}>{v.nombre}</div>
                    <div style={{ fontSize: 11, color: '#5a7a8a' }}>{fmtFecha(v.fecha)} · <span style={{ color: '#6366f1', fontWeight: 600 }}>½ día compensatorio</span></div>
                  </div>
                </div>
                <button onClick={() => borrarDia(v.id)} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Borrar</button>
              </div>
            ))}
          </div>
        )}
        {data.votos.length === 0 && !showVotoForm && (
          <div style={{ fontSize: 11, color: '#b3b3b3', textAlign: 'center', padding: '4px 0' }}>Sin días de voto registrados en {anio}</div>
        )}

        {showVotoForm && (
          <form onSubmit={agregarVoto} style={{ marginTop: 8, padding: '12px', background: '#f9fafb', borderRadius: 2 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
              <div>
                <label className="label">Tipo de elección *</label>
                <input className="input" value={votoForm.nombre} style={inputStyle} required placeholder="Ej: Presidencia 1a vuelta"
                  onChange={e => setVotoForm(f => ({ ...f, nombre: e.target.value }))} />
              </div>
              <div>
                <label className="label">Fecha *</label>
                <input className="input" type="date" value={votoForm.fecha} style={inputStyle} required
                  onChange={e => setVotoForm(f => ({ ...f, fecha: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" className="btn-primary" style={{ flex: 1, padding: '7px', fontSize: 12 }}>Guardar</button>
              <button type="button" className="btn-secondary" onClick={() => setShowVotoForm(false)}>Cancelar</button>
            </div>
          </form>
        )}
      </div>

      {/* ── Día de familia ── */}
      <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '16px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#08293f' }}>Día de la familia {anio}</div>
            <div style={{ fontSize: 10, color: '#5a7a8a', marginTop: 2 }}>Ley 1857/2017 · 1 día libre por año</div>
          </div>
          {data.familia && (
            <button onClick={() => borrarDia(data.familia.id)} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Borrar</button>
          )}
        </div>

        {data.familia ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(5,150,105,0.06)', borderRadius: 2, border: '1px solid rgba(5,150,105,0.2)' }}>
            <span style={{ fontSize: 20 }}>✓</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#059669' }}>Utilizado</div>
              <div style={{ fontSize: 11, color: '#5a7a8a' }}>{fmtFecha(data.familia.fecha)}</div>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 11, color: '#b45309', fontWeight: 600, marginBottom: 10 }}>⚠ Pendiente por tomar</div>
            <form onSubmit={guardarFamilia} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label className="label">Fecha en que lo tomó</label>
                <input className="input" type="date" value={familiaFecha} style={inputStyle}
                  onChange={e => setFamiliaFecha(e.target.value)} />
              </div>
              <button type="submit" className="btn-primary" style={{ padding: '8px 16px', fontSize: 12, whiteSpace: 'nowrap' }}>
                Marcar como tomado
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// PANEL PRIMA LEGAL
// ════════════════════════════════════════════════════════════════
function PrimaLegalPanel({ empleados, refreshKey }) {
  const [primas,    setPrimas]    = useState([]);
  const [showForm,  setShowForm]  = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [msg,       setMsg]       = useState({ text: '', ok: true });
  const [form,      setForm]      = useState({ empleado_id: '', fecha: '', valor: '', notas: '' });

  const cargar = useCallback(() => {
    API.get('/nomina/primas').then(r => setPrimas(r.data)).catch(() => {});
  }, [refreshKey]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);

  const showMsg = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg({ text: '', ok: true }), 4000); };

  const guardar = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await API.post('/nomina/prima', form);
      setShowForm(false); setForm({ empleado_id: '', fecha: '', valor: '', notas: '' });
      showMsg('✓ Prima legal registrada — gasto creado');
      cargar();
    } catch (err) { showMsg('Error: ' + (err.response?.data?.error || err.message), false); }
    finally { setLoading(false); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta prima legal? También se eliminará el gasto asociado.')) return;
    await API.delete(`/nomina/prima/${id}`); showMsg('Prima eliminada'); cargar();
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '16px 18px', marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#08293f' }}>Prima Legal</div>
          <div style={{ fontSize: 10, color: '#5a7a8a', marginTop: 2 }}>Se paga en junio y diciembre — crea gasto automáticamente</div>
        </div>
        <button onClick={() => setShowForm(v => !v)} style={{ fontSize: 11, fontWeight: 600, color: '#00b8e3', background: 'none', border: 'none', cursor: 'pointer' }}>
          {showForm ? '× Cancelar' : '+ Registrar prima'}
        </button>
      </div>

      {msg.text && (
        <div style={{ marginBottom: 10, padding: '7px 12px', fontSize: 11, fontWeight: 600, borderRadius: 2,
          background: msg.ok ? 'rgba(5,150,105,0.08)' : 'rgba(251,191,36,0.1)',
          border: `1px solid ${msg.ok ? 'rgba(5,150,105,0.2)' : 'rgba(251,191,36,0.3)'}`,
          color: msg.ok ? '#059669' : '#b45309' }}>{msg.text}</div>
      )}

      {showForm && (
        <form onSubmit={guardar} style={{ background: '#f9fafb', borderRadius: 2, padding: 12, marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 10 }}>
            <div>
              <label className="label">Empleado *</label>
              <select className="input" value={form.empleado_id} style={inputStyle} required
                onChange={e => setForm(f => ({ ...f, empleado_id: e.target.value }))}>
                <option value="">Seleccionar...</option>
                {empleados.map(emp => <option key={emp.id} value={emp.id}>{emp.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Fecha *</label>
              <input className="input" type="date" value={form.fecha} style={inputStyle} required
                onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
            </div>
            <div>
              <label className="label">Valor *</label>
              <input className="input" type="number" value={form.valor} style={inputStyle} required min="0"
                placeholder="0" onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} />
            </div>
            <div>
              <label className="label">Notas</label>
              <input className="input" value={form.notas} style={inputStyle} placeholder="Opcional"
                onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn-primary" disabled={loading} style={{ padding: '7px 16px', fontSize: 12 }}>Guardar</button>
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
          </div>
        </form>
      )}

      {primas.length === 0 && !showForm && (
        <div style={{ fontSize: 11, color: '#b3b3b3', textAlign: 'center', padding: '8px 0' }}>Sin primas registradas</div>
      )}

      {primas.map(p => (
        <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px',
          background: '#f9fafb', borderRadius: 2, border: '1px solid #e8eef2', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#08293f' }}>{p.mes}</div>
            <div style={{ fontSize: 12, color: '#5a7a8a', marginTop: 2 }}>
              {p.empleado_nombre} · {fmt(p.valor)}
              {p.notas && <span style={{ marginLeft: 8, color: '#b3b3b3' }}>· {p.notas}</span>}
            </div>
          </div>
          <button onClick={() => eliminar(p.id)} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Borrar</button>
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// PANEL CESANTÍAS
// ════════════════════════════════════════════════════════════════
function CesantiasPanel({ empleadoId, refreshKey }) {
  const [lista,     setLista]     = useState([]);
  const [showForm,  setShowForm]  = useState(false);
  const [form,      setForm]      = useState({ anio: new Date().getFullYear(), valor: '', fecha_pago: '', notas: '' });
  const [loading,   setLoading]   = useState(false);
  const [msg,       setMsg]       = useState({ text: '', ok: true });

  const cargar = useCallback(() => {
    API.get(`/empleados/${empleadoId}/cesantias`).then(r => setLista(r.data)).catch(() => {});
  }, [empleadoId, refreshKey]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);

  const showMsg = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg({ text: '', ok: true }), 4000); };

  const guardar = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await API.post(`/empleados/${empleadoId}/cesantias`, form);
      setShowForm(false); setForm({ anio: new Date().getFullYear(), valor: '', fecha_pago: '', notas: '' });
      cargar();
    } catch (err) { showMsg('Error: ' + (err.response?.data?.error || err.message), false); }
    finally { setLoading(false); }
  };

  const marcarPagado = async (ces) => {
    if (ces.pagado) return;
    if (!window.confirm(`¿Marcar cesantías ${ces.anio} como pagadas? Se registrará un gasto de ${fmt(ces.valor)}.`)) return;
    setLoading(true);
    try {
      await API.put(`/empleados/${empleadoId}/cesantias/${ces.id}`, {
        valor: ces.valor, fecha_pago: ces.fecha_pago || new Date().toISOString().slice(0,10), pagado: true, notas: ces.notas
      });
      showMsg('✓ Cesantías marcadas como pagadas — gasto registrado');
      cargar();
    } catch (err) { showMsg('Error: ' + (err.response?.data?.error || err.message), false); }
    finally { setLoading(false); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar registro de cesantías?')) return;
    await API.delete(`/empleados/${empleadoId}/cesantias/${id}`); cargar();
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '16px 18px', marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#08293f' }}>Cesantías</div>
          <div style={{ fontSize: 10, color: '#5a7a8a', marginTop: 2 }}>Liquidación anual — cuenta como gasto al pagar</div>
        </div>
        <button onClick={() => setShowForm(v => !v)} style={{ fontSize: 11, fontWeight: 600, color: '#00b8e3', background: 'none', border: 'none', cursor: 'pointer' }}>
          {showForm ? '× Cancelar' : '+ Registrar cesantías'}
        </button>
      </div>

      {msg.text && (
        <div style={{ marginBottom: 10, padding: '7px 12px', fontSize: 11, fontWeight: 600, borderRadius: 2,
          background: msg.ok ? 'rgba(5,150,105,0.08)' : 'rgba(251,191,36,0.1)',
          border: `1px solid ${msg.ok ? 'rgba(5,150,105,0.2)' : 'rgba(251,191,36,0.3)'}`,
          color: msg.ok ? '#059669' : '#b45309' }}>{msg.text}</div>
      )}

      {showForm && (
        <form onSubmit={guardar} style={{ background: '#f9fafb', borderRadius: 2, padding: 12, marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 10 }}>
            <div>
              <label className="label">Año *</label>
              <input className="input" type="number" value={form.anio} style={inputStyle} required min="2020" max="2099"
                onChange={e => setForm(f => ({ ...f, anio: e.target.value }))} />
            </div>
            <div>
              <label className="label">Valor cesantías *</label>
              <input className="input" type="number" value={form.valor} style={inputStyle} required min="0"
                placeholder="0" onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} />
            </div>
            <div>
              <label className="label">Fecha de pago</label>
              <input className="input" type="date" value={form.fecha_pago} style={inputStyle}
                onChange={e => setForm(f => ({ ...f, fecha_pago: e.target.value }))} />
            </div>
            <div>
              <label className="label">Notas</label>
              <input className="input" value={form.notas} style={inputStyle} placeholder="Opcional"
                onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn-primary" disabled={loading} style={{ padding: '7px 16px', fontSize: 12 }}>Guardar</button>
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
          </div>
        </form>
      )}

      {lista.length === 0 && !showForm && (
        <div style={{ fontSize: 11, color: '#b3b3b3', textAlign: 'center', padding: '8px 0' }}>Sin cesantías registradas</div>
      )}

      {lista.map(ces => (
        <div key={ces.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: ces.pagado ? 'rgba(5,150,105,0.05)' : '#f9fafb', borderRadius: 2, border: `1px solid ${ces.pagado ? 'rgba(5,150,105,0.2)' : '#e8eef2'}`, marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#08293f' }}>Año {ces.anio}</div>
            <div style={{ fontSize: 12, color: '#5a7a8a', marginTop: 2 }}>
              {fmt(ces.valor)}
              {ces.fecha_pago && <span style={{ marginLeft: 8 }}>· Fecha: {String(ces.fecha_pago).slice(0,10)}</span>}
              {ces.notas && <span style={{ marginLeft: 8, color: '#b3b3b3' }}>· {ces.notas}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {ces.pagado ? (
              <span style={{ fontSize: 11, fontWeight: 600, color: '#059669', background: 'rgba(5,150,105,0.1)', padding: '3px 10px', borderRadius: 20 }}>✓ Pagado</span>
            ) : (
              <button onClick={() => marcarPagado(ces)} disabled={loading} style={{ fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 2, border: '1px solid rgba(5,150,105,0.3)', background: 'rgba(5,150,105,0.08)', color: '#059669', cursor: 'pointer' }}>
                Marcar como pagado
              </button>
            )}
            {!ces.pagado && <button onClick={() => eliminar(ces.id)} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Borrar</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

const LOGO_AGE_SVG = `<svg class="logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="#08293f"/><path d="M15 47 27 17" fill="none" stroke="#00b8e3" stroke-width="6" stroke-linecap="round"/><path d="M31 47 43 17" fill="none" stroke="#00b8e3" stroke-width="6" stroke-linecap="round"/></svg>`;

// Comprobante imprimible. c: valores de la liquidación (del cálculo o de un registro del historial).
function imprimirComprobante(emp, periodo, q, c) {
  const calc = c;
  // salario_base del empleado ya incluye el auxilio de transporte: el salario base real es la resta.
  const totalMes = parseFloat(emp.salario_base) || 0;
  const auxMes = parseFloat(emp.aux_transporte) || 0;
  const baseMes = totalMes - auxMes;
  const baseQ = Math.round(baseMes / 2);
  const auxQ = Math.round(Number(calc.quincena_base) || 0) - baseQ;
  const mesStr = mesLabel(periodo);
  const qStr = q === 1 ? '1a Quincena' : '2a Quincena';
  const hoy = new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Comprobante ${mesStr} ${qStr}</title>
<style>
  @page { size: 5.5in 8.5in; margin: 0.5in; }
  * { box-sizing: border-box; margin: 0; padding: 0; font-family: Arial, sans-serif; }
  body { font-size: 11px; color: #1a1a2e; }
  .header { display: flex; align-items: center; gap: 14px; border-bottom: 2px solid #00b8e3; padding-bottom: 12px; margin-bottom: 14px; }
  .logo { width: 52px; height: 52px; flex: none; }
  .company { flex: 1; }
  .company-name { font-size: 15px; font-weight: 700; color: #08293f; }
  .company-sub { font-size: 9px; color: #5a7a8a; letter-spacing: 0.06em; text-transform: uppercase; margin-top: 2px; }
  .doc-title { font-size: 13px; font-weight: 700; color: #08293f; text-align: right; }
  .doc-period { font-size: 10px; color: #5a7a8a; text-align: right; margin-top: 2px; }
  .section { background: #f9fafb; border: 1px solid #e8eef2; border-radius: 3px; padding: 10px 14px; margin-bottom: 10px; }
  .section-title { font-size: 8px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #5a7a8a; margin-bottom: 8px; }
  .row { display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #f0f0f0; }
  .row:last-child { border-bottom: none; }
  .row-label { color: #5a7a8a; }
  .row-value { font-weight: 600; color: #08293f; }
  .row-value.neg { color: #dc2626; }
  .row-value.pos { color: #059669; }
  .total-box { background: #08293f; color: #fff; border-radius: 3px; padding: 12px 14px; margin-bottom: 10px; display: flex; justify-content: space-between; align-items: center; }
  .total-label { font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(0,184,227,0.6); margin-bottom: 4px; }
  .total-value { font-size: 20px; font-weight: 700; color: #00b8e3; }
  .emp-info { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 10px; }
  .info-item { background: #f9fafb; border: 1px solid #e8eef2; padding: 7px 10px; border-radius: 3px; }
  .info-label { font-size: 8px; color: #5a7a8a; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 2px; }
  .info-value { font-size: 12px; font-weight: 700; color: #08293f; }
  .employer-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 6px; }
  .emp-kpi { background: #f9fafb; border: 1px solid #e8eef2; padding: 7px 10px; border-radius: 3px; text-align: center; }
  .emp-kpi-label { font-size: 8px; color: #5a7a8a; margin-bottom: 2px; }
  .emp-kpi-val { font-size: 11px; font-weight: 700; color: #b45309; }
  .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 30px; }
  .sig-line { border-top: 1px solid #08293f; padding-top: 6px; text-align: center; font-size: 9px; color: #5a7a8a; }
  .footer { text-align: center; font-size: 8px; color: #b3b3b3; margin-top: 14px; border-top: 1px solid #e8eef2; padding-top: 8px; }
</style></head><body>
<div class="header">
  ${LOGO_AGE_SVG}
  <div class="company">
    <div class="company-name">Ageinformática</div>
    <div class="company-sub">Comprobante de Pago · Nómina</div>
  </div>
  <div>
    <div class="doc-title">${qStr}</div>
    <div class="doc-period">${mesStr}</div>
    <div class="doc-period" style="margin-top:2px">Emitido: ${hoy}</div>
  </div>
</div>

<div class="emp-info">
  <div class="info-item"><div class="info-label">Empleado</div><div class="info-value">${emp.nombre}</div></div>
  <div class="info-item"><div class="info-label">Cédula</div><div class="info-value">${emp.cedula}</div></div>
  <div class="info-item"><div class="info-label">Salario base</div><div class="info-value">${fmt(baseMes)}</div></div>
  <div class="info-item"><div class="info-label">Aux. transporte</div><div class="info-value">${fmt(emp.aux_transporte)}</div></div>
</div>

<div class="section">
  <div class="section-title">Devengado y deducciones — ${qStr}</div>
  <div class="row"><span class="row-label">Salario quincena (salario base / 2)</span><span class="row-value">${fmt(baseQ)}</span></div>
  ${auxMes > 0 ? `<div class="row"><span class="row-label">Auxilio de transporte (quincena)</span><span class="row-value">${fmt(auxQ)}</span></div>` : ''}
  <div class="row"><span class="row-label">Salud 4% (deducción empleado)</span><span class="row-value neg">− ${fmt(calc.salud_empleado)}</span></div>
  <div class="row"><span class="row-label">Pensión 4% (deducción empleado)</span><span class="row-value neg">− ${fmt(calc.pension_empleado)}</span></div>
</div>

<div class="total-box">
  <div>
    <div class="total-label">Neto a pagar</div>
    <div class="total-value">${fmt(calc.neto_quincena)}</div>
  </div>
  <div style="text-align:right">
    <div style="font-size:9px;color:rgba(255,255,255,0.4)">Neto mensual</div>
    <div style="font-size:13px;font-weight:700;color:rgba(255,255,255,0.7)">${fmt(calc.neto_mensual)}</div>
    <div style="font-size:9px;color:rgba(255,255,255,0.4);margin-top:4px">Salario + auxilio mensual</div>
    <div style="font-size:13px;font-weight:700;color:rgba(255,255,255,0.7)">${fmt(totalMes)}</div>
  </div>
</div>

<div class="section">
  <div class="section-title">Aportes empleador (costo mensual)</div>
  <div class="employer-grid">
    <div class="emp-kpi"><div class="emp-kpi-label">Salud 8.5%</div><div class="emp-kpi-val">${fmt(calc.salud_empleador)}</div></div>
    <div class="emp-kpi"><div class="emp-kpi-label">Pensión 12%</div><div class="emp-kpi-val">${fmt(calc.pension_empleador)}</div></div>
    <div class="emp-kpi"><div class="emp-kpi-label">ARL 0.52%</div><div class="emp-kpi-val">${fmt(calc.arl)}</div></div>
    <div class="emp-kpi"><div class="emp-kpi-label">Caja 4%</div><div class="emp-kpi-val">${fmt(calc.caja)}</div></div>
    <div class="emp-kpi"><div class="emp-kpi-label">SENA 2%</div><div class="emp-kpi-val">${fmt(calc.sena)}</div></div>
    <div class="emp-kpi"><div class="emp-kpi-label">ICBF 3%</div><div class="emp-kpi-val">${fmt(calc.icbf)}</div></div>
  </div>
  <div class="row" style="margin-top:8px;padding-top:8px;border-top:1px solid #e8eef2">
    <span class="row-label" style="font-weight:700">Costo total empleador mensual</span>
    <span class="row-value" style="color:#b45309;font-size:13px">${fmt(calc.costo_total)}</span>
  </div>
</div>

<div class="signatures">
  <div class="sig-line">Firma empleador / Representante legal</div>
  <div class="sig-line">Firma empleado: ${emp.nombre}</div>
</div>

<div class="footer">Ageinformática · Comprobante generado el ${hoy} · ${mesStr} ${qStr}</div>
</body></html>`;
  const w = window.open('', '_blank', 'width=600,height=900');
  w.document.write(html);
  w.document.close();
  w.onload = () => { w.print(); };
}

// Normaliza una fila de /nomina (historial) al formato de /nomina/calcular
function calcDesdeRegistro(n) {
  return {
    quincena_base: parseFloat(n.salario_base || 0) / 2,
    salud_empleado: n.salud_empleado, pension_empleado: n.pension_empleado,
    neto_quincena: n.neto_pagar, neto_mensual: parseFloat(n.neto_pagar || 0) * 2, devengado: n.devengado,
    salud_empleador: n.salud_empleador, pension_empleador: n.pension_empleador,
    arl: n.arl, caja: n.caja, sena: n.sena, icbf: n.icbf, costo_total: n.costo_total,
  };
}

// ════════════════════════════════════════════════════════════════
// NÓMINA — vista única (menú Empresas y nómina → Nómina)
// ════════════════════════════════════════════════════════════════
export function NominaPage() {
  const [empleados, setEmpleados] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [calc,      setCalc]      = useState(null);
  const [periodo,   setPeriodo]   = useState(nowPeriodo());
  const [loading,   setLoading]   = useState(false);
  const [msg,       setMsg]       = useState({ text: '', ok: true });
  const [jpgNomina,  setJpgNomina]  = useState(null);
  const [showEmpForm,   setShowEmpForm]   = useState(false);
  const [editEmpId,     setEditEmpId]     = useState(null);
  const [showPasados,   setShowPasados]   = useState(false);
  const [reloadKey,     setReloadKey]     = useState(0);
  const [aniosExpandidos, setAniosExpandidos] = useState({});
  const [mesFiltroAnio,   setMesFiltroAnio]   = useState({});
  const emptyEmp = { cedula: '', nombre: '', salario_base: '', aux_transporte: '', fecha_ingreso: '' };
  const [empForm, setEmpForm] = useState(emptyEmp);

  const cargar = useCallback(() => {
    API.get('/empleados').then(r => {
      setEmpleados(r.data);
      if (r.data.length) calcularEmp(r.data[0].id);
    });
    API.get('/nomina').then(r => setHistorial(r.data));
  }, []); // eslint-disable-line

  useEffect(() => { cargar(); }, [cargar]);

  async function calcularEmp(id) {
    try {
      const { data } = await API.post('/nomina/calcular', { empleado_id: id });
      setCalc(data);
    } catch { setCalc(null); }
  }

  const showMsg = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg({ text: '', ok: true }), 4000); };

  const printComprobante = q => { if (calc) imprimirComprobante(calc.empleado, periodo, q, calc); };
  const abrirJpg = q => { if (calc) setJpgNomina({ emp: calc.empleado, periodo, q, calc }); };

  async function registrarQuincena(q, periodoOverride) {
    if (!calc) return;
    const per = periodoOverride || periodo;
    setLoading(true);
    try {
      await API.post('/nomina/registrar', { empleado_id: calc.empleado.id, quincena: q, periodo: per });
      showMsg(`✓ ${q === 1 ? '1a' : '2a'} quincena ${mesLabel(per)} registrada`);
      API.get('/nomina').then(r => setHistorial(r.data));
    } catch (err) {
      const e = err.response?.data?.error;
      showMsg(e === 'YA_REGISTRADO' ? `⚠ Ya registrada: ${mesLabel(per)} Q${q}` : 'Error: ' + e, false);
    } finally { setLoading(false); }
  }

  async function guardarEmp(e) {
    e.preventDefault(); setLoading(true);
    try {
      if (editEmpId) await API.put(`/empleados/${editEmpId}`, empForm);
      else           await API.post('/empleados', empForm);
      setShowEmpForm(false); setEditEmpId(null); setEmpForm(emptyEmp);
      setReloadKey(k => k + 1); cargar();
    } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  }

  function editarEmp(emp) {
    setEmpForm({ cedula: emp.cedula, nombre: emp.nombre, salario_base: emp.salario_base,
      aux_transporte: emp.aux_transporte, fecha_ingreso: emp.fecha_ingreso?.slice(0,10) || '' });
    setEditEmpId(emp.id); setShowEmpForm(true);
  }

  // Periodos ya registrados (set de "periodo-quincena")
  const registrados = new Set(historial.map(n => `${n.periodo}-${n.quincena}`));

  return (
    <div>
      {jpgNomina && <ModalNominaJpg empleado={jpgNomina.emp} calc={jpgNomina.calc} mesTexto={mesLabel(jpgNomina.periodo)} qTexto={jpgNomina.q === 1 ? '1a Quincena' : '2a Quincena'} onClose={() => setJpgNomina(null)} />}
      {msg.text && (
        <div style={{ marginBottom: 12, padding: '9px 14px', fontSize: 12, fontWeight: 600, borderRadius: 2,
          background: msg.ok ? 'rgba(5,150,105,0.08)' : 'rgba(251,191,36,0.1)',
          border: `1px solid ${msg.ok ? 'rgba(5,150,105,0.25)' : 'rgba(251,191,36,0.3)'}`,
          color: msg.ok ? '#059669' : '#b45309' }}>{msg.text}</div>
      )}

      {/* Empleados */}
      {empleados.map(emp => (
        <div key={emp.id} style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '16px 18px', marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ fontWeight: 700, color: '#08293f', fontSize: 15 }}>{emp.nombre}</div>
              <div style={{ fontSize: 11, color: '#5a7a8a', marginTop: 2 }}>CC {emp.cedula}</div>
              {emp.fecha_ingreso && (
                <div style={{ fontSize: 11, color: '#00b8e3', marginTop: 3 }}>
                  Ingreso: {String(emp.fecha_ingreso).slice(0,10)}
                </div>
              )}
            </div>
            <button onClick={() => editarEmp(emp)} style={{ fontSize: 11, color: '#00b8e3', background: 'none', border: 'none', cursor: 'pointer' }}>Editar</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }} className="nomina-kpi">
            {[
              { label: 'Salario base',    value: fmt(emp.salario_base)    },
              { label: 'Aux transporte',  value: fmt(emp.aux_transporte)  },
              { label: 'Devengado',       value: fmt(parseFloat(emp.salario_base) + parseFloat(emp.aux_transporte)) },
            ].map((k,i) => (
              <div key={i} style={{ background: '#f4f7fa', padding: '10px 12px', borderRadius: 2 }}>
                <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 3 }}>{k.label}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#08293f' }}>{k.value}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {!empleados.length && (
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#b3b3b3', fontSize: 12, marginBottom: 12 }}>
          No hay empleados registrados.
        </div>
      )}

      <button className="btn-secondary" style={{ marginBottom: 16, fontSize: 12 }}
        onClick={() => { setEmpForm(emptyEmp); setEditEmpId(null); setShowEmpForm(true); }}>
        + Agregar empleado
      </button>

      {/* Calculadora de quincena */}
      {calc && (
        <div style={{ background: '#08293f', border: '1px solid rgba(0,184,227,0.2)', borderRadius: 2, padding: '18px 20px', marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(0,184,227,0.6)', marginBottom: 14 }}>— Liquidación quincena</div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Período:</label>
            <input type="month" value={periodo} onChange={e => setPeriodo(e.target.value)}
              className="input" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(0,184,227,0.2)', color: '#fff', width: 'auto', fontSize: 12 }} />
          </div>

          {/* Liquidación quincena */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(0,184,227,0.5)', marginBottom: 6 }}>Desglose quincena</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, background: 'rgba(255,255,255,0.04)', borderRadius: 2, overflow: 'hidden' }}>
              {[
                { label: 'Quincena base (salario / 2)',   value: fmt(calc.quincena_base),      color: '#fff'   },
                { label: 'Salud 4%  (deducción)',         value: '− ' + fmt(calc.salud_empleado),   color: '#f87171' },
                { label: 'Pensión 4%  (deducción)',       value: '− ' + fmt(calc.pension_empleado), color: '#f87171' },
              ].map((k, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px',
                  borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{k.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: k.color }}>{k.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Neto quincena — destacado */}
          <div className="nomina-total-box" style={{ background: 'rgba(0,184,227,0.1)', border: '1px solid rgba(0,184,227,0.2)', borderRadius: 2, padding: '12px 16px', marginBottom: 14,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(0,184,227,0.6)', marginBottom: 4 }}>Total a pagar quincena</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#00b8e3' }}>{fmt(calc.neto_quincena)}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>Neto mensual: {fmt(calc.neto_mensual)} · Devengado: {fmt(calc.devengado)}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => registrarQuincena(1)} disabled={loading || registrados.has(`${periodo}-1`)}
                  style={{ flex: 1, fontSize: 12, fontWeight: 600, padding: '8px 14px', borderRadius: 2, cursor: 'pointer',
                    background: registrados.has(`${periodo}-1`) ? 'rgba(255,255,255,0.05)' : '#00b8e3',
                    color: registrados.has(`${periodo}-1`) ? 'rgba(255,255,255,0.3)' : '#08293f',
                    border: 'none' }}>
                  {registrados.has(`${periodo}-1`) ? '✓ 1a registrada' : '⚡ Registrar 1a quincena'}
                </button>
                <button onClick={() => abrirJpg(1)} title="Descargar comprobante 1a quincena en JPG (WhatsApp)"
                  style={{ fontSize: 11, fontWeight: 700, padding: '8px 10px', borderRadius: 2, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(0,184,227,0.2)', color: '#00b8e3', cursor: 'pointer' }}>
                  JPG
                </button>
                <button onClick={() => printComprobante(1)} title="Imprimir comprobante 1a quincena"
                  style={{ fontSize: 14, padding: '8px 10px', borderRadius: 2, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(0,184,227,0.2)', color: '#00b8e3', cursor: 'pointer' }}>
                  🖨
                </button>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => registrarQuincena(2)} disabled={loading || registrados.has(`${periodo}-2`)}
                  style={{ flex: 1, fontSize: 12, fontWeight: 600, padding: '8px 14px', borderRadius: 2, cursor: 'pointer',
                    background: registrados.has(`${periodo}-2`) ? 'rgba(255,255,255,0.05)' : 'rgba(0,184,227,0.15)',
                    color: registrados.has(`${periodo}-2`) ? 'rgba(255,255,255,0.3)' : '#00b8e3',
                    border: `1px solid ${registrados.has(`${periodo}-2`) ? 'transparent' : 'rgba(0,184,227,0.3)'}` }}>
                  {registrados.has(`${periodo}-2`) ? '✓ 2a registrada' : '⚡ Registrar 2a quincena'}
                </button>
                <button onClick={() => abrirJpg(2)} title="Descargar comprobante 2a quincena en JPG (WhatsApp)"
                  style={{ fontSize: 11, fontWeight: 700, padding: '8px 10px', borderRadius: 2, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(0,184,227,0.2)', color: '#00b8e3', cursor: 'pointer' }}>
                  JPG
                </button>
                <button onClick={() => printComprobante(2)} title="Imprimir comprobante 2a quincena"
                  style={{ fontSize: 14, padding: '8px 10px', borderRadius: 2, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(0,184,227,0.2)', color: '#00b8e3', cursor: 'pointer' }}>
                  🖨
                </button>
              </div>
            </div>
          </div>

          {/* Aportes empleador */}
          <div>
            <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(251,191,36,0.7)', marginBottom: 6 }}>Aportes empleador (mensual)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }} className="nomina-kpi">
              {[
                { label: 'Salud 8.5%',   value: fmt(calc.salud_empleador) },
                { label: 'Pensión 12%',  value: fmt(calc.pension_empleador) },
                { label: 'ARL 0.52%',    value: fmt(calc.arl) },
                { label: 'Caja 4%',      value: fmt(calc.caja) },
                { label: 'SENA 2%',      value: fmt(calc.sena) },
                { label: 'ICBF 3%',      value: fmt(calc.icbf) },
              ].map((k,i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.04)', padding: '7px 10px', borderRadius: 2 }}>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginBottom: 2 }}>{k.label}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(251,191,36,0.8)' }}>{k.value}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Costo total empleador mensual</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#fbbf24' }}>{fmt(calc.costo_total)}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Períodos anteriores ── */}
      {calc && (() => {
        const hoyNow = new Date();
        const periodoActual = hoyNow.getFullYear() + '-' + String(hoyNow.getMonth() + 1).padStart(2, '0');
        const periodosMes = [];
        // Generate Jan 2025 → month before current
        let y = 2025, m = 1;
        while (true) {
          const p = y + '-' + String(m).padStart(2, '0');
          if (p >= periodoActual) break;
          periodosMes.push(p);
          m++; if (m > 12) { m = 1; y++; }
        }
        if (!periodosMes.length) return null;
        return (
          <div style={{ marginBottom: 16 }}>
            <button onClick={() => setShowPasados(v => !v)} style={{
              width: '100%', textAlign: 'left', padding: '10px 14px', fontSize: 12, fontWeight: 600,
              background: '#f4f7fa', border: '1px solid #e8eef2', borderRadius: 2, cursor: 'pointer',
              color: '#08293f', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <span>Registrar períodos anteriores</span>
              <span style={{ color: '#5a7a8a' }}>{showPasados ? '▲' : '▼'}</span>
            </button>
            {showPasados && (
              <div style={{ border: '1px solid #e8eef2', borderTop: 'none', borderRadius: '0 0 2px 2px', padding: '12px 14px', background: '#fff' }}>
                <p style={{ fontSize: 11, color: '#5a7a8a', marginTop: 0, marginBottom: 12 }}>
                  Registra quincenas pasadas con los valores actuales del empleado ({calc.empleado.nombre}).
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {periodosMes.map(p => {
                    const q1 = registrados.has(`${p}-1`);
                    const q2 = registrados.has(`${p}-2`);
                    return (
                      <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f9fafb', borderRadius: 2, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#08293f', minWidth: 120 }}>{mesLabel(p)}</span>
                        <button onClick={() => registrarQuincena(1, p)} disabled={loading || q1}
                          style={{ fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 2, cursor: q1 ? 'default' : 'pointer', border: 'none',
                            background: q1 ? '#e8eef2' : '#08293f', color: q1 ? '#b3b3b3' : '#00b8e3' }}>
                          {q1 ? '✓ 1a Q' : '+ 1a quincena'}
                        </button>
                        <button onClick={() => registrarQuincena(2, p)} disabled={loading || q2}
                          style={{ fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 2, cursor: q2 ? 'default' : 'pointer', border: '1px solid',
                            borderColor: q2 ? 'transparent' : 'rgba(0,184,227,0.3)',
                            background: q2 ? '#e8eef2' : 'rgba(0,184,227,0.08)', color: q2 ? '#b3b3b3' : '#00b8e3' }}>
                          {q2 ? '✓ 2a Q' : '+ 2a quincena'}
                        </button>
                        {q1 && q2 && <span style={{ fontSize: 10, color: '#059669', fontWeight: 600 }}>✓ Completo</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Historial nómina — agrupado por año */}
      {historial.length > 0 && (() => {
        const anioActualHist = new Date().getFullYear();
        const mesActualHist  = new Date().getMonth() + 1;
        const periodoActualHist = anioActualHist + '-' + String(mesActualHist).padStart(2,'0');
        // Agrupar por año
        const porAnio = {};
        historial.forEach(n => {
          const anio = parseInt(n.periodo?.split('-')[0] || new Date().getFullYear());
          if (!porAnio[anio]) porAnio[anio] = [];
          porAnio[anio].push(n);
        });
        const aniosOrden = Object.keys(porAnio).map(Number).sort((a,b) => b - a);
        const toggleAnio = (a) => setAniosExpandidos(prev => ({ ...prev, [a]: !prev[a] }));
        const toggleMesFiltro = (a) => setMesFiltroAnio(prev => {
          const next = { ...prev };
          if (next[a]) delete next[a]; else next[a] = 'todos';
          return next;
        });
        return (
          <div className="card" style={{ padding: 0, marginBottom: 12 }}>
            <div style={{ padding: '12px 16px', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', borderBottom: '1px solid #e8eef2' }}>— Historial pagos</div>
            {aniosOrden.map(anio => {
              const expanded = !!aniosExpandidos[anio];
              const mostrarTodos = mesFiltroAnio[anio] === 'todos';
              let filas = porAnio[anio];
              if (!mostrarTodos && anio === anioActualHist) {
                filas = filas.filter(n => n.periodo === periodoActualHist);
              }
              const totalAnio = porAnio[anio].reduce((s, n) => s + parseFloat(n.neto_pagar || 0), 0);
              return (
                <div key={anio} style={{ borderBottom: '1px solid #e8eef2' }}>
                  <button onClick={() => toggleAnio(anio)} style={{
                    width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#08293f' }}>{anio}</span>
                      <span style={{ fontSize: 10, color: '#5a7a8a' }}>{porAnio[anio].length} registros · Neto pagado: <strong style={{ color: '#059669' }}>{fmt(totalAnio)}</strong></span>
                    </div>
                    <span style={{ fontSize: 12, color: '#5a7a8a' }}>{expanded ? '▲' : '▼'}</span>
                  </button>
                  {expanded && (
                    <div style={{ borderTop: '1px solid #f0f0f0' }}>
                      {anio === anioActualHist && (
                        <div style={{ padding: '6px 16px', background: '#f9fafb' }}>
                          <button onClick={() => toggleMesFiltro(anio)} style={{ fontSize: 10, fontWeight: 600, color: '#00b8e3', background: 'none', border: 'none', cursor: 'pointer' }}>
                            {mostrarTodos ? 'Ver solo mes actual' : 'Ver todo el año'}
                          </button>
                        </div>
                      )}
                      <div style={{ overflowX: 'auto' }}>
                        <table className="tbl">
                          <thead>
                            <tr>
                              <th>Período</th><th>Empleado</th><th>Q</th>
                              <th style={{ textAlign: 'right' }}>Devengado</th>
                              <th style={{ textAlign: 'right' }}>Deducciones</th>
                              <th style={{ textAlign: 'right' }}>Neto pagado</th>
                              <th style={{ textAlign: 'right' }}>Costo total</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {filas.map(n => (
                              <tr key={n.id}>
                                <td style={{ color: '#5a7a8a', fontSize: 12 }}>{n.mes}</td>
                                <td style={{ fontWeight: 600, color: '#08293f' }}>{n.empleado_nombre}</td>
                                <td><span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', background: 'rgba(0,184,227,0.08)', color: '#00b8e3', borderRadius: 2 }}>{n.quincena === 1 ? '1a' : '2a'}</span></td>
                                <td style={{ textAlign: 'right', color: '#08293f' }}>{fmt(n.devengado)}</td>
                                <td style={{ textAlign: 'right', color: '#dc2626' }}>{fmt(n.total_deducciones)}</td>
                                <td style={{ textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmt(n.neto_pagar)}</td>
                                <td style={{ textAlign: 'right', fontWeight: 700, color: '#b45309' }}>{fmt(n.costo_total)}</td>
                                <td style={{ textAlign: 'right' }}>
                                  <button onClick={() => setJpgNomina({ emp: { nombre: n.empleado_nombre, cedula: n.cedula, salario_base: n.salario_base, aux_transporte: n.aux_transporte }, periodo: n.periodo, q: n.quincena, calc: calcDesdeRegistro(n) })}
                                    title="Descargar comprobante en JPG (WhatsApp)" style={{ background: 'none', border: 'none', color: '#00b8e3', cursor: 'pointer', fontSize: 11, fontWeight: 700, marginRight: 6 }}>JPG</button>
                                  <button onClick={() => imprimirComprobante(
                                    { nombre: n.empleado_nombre, cedula: n.cedula, salario_base: n.salario_base, aux_transporte: n.aux_transporte },
                                    n.periodo, n.quincena, calcDesdeRegistro(n))}
                                    title="Imprimir comprobante" style={{ background: 'none', border: 'none', color: '#00b8e3', cursor: 'pointer', fontSize: 14 }}>🖨</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {filas.length === 0 && <div style={{ textAlign: 'center', padding: '14px 0', color: '#b3b3b3', fontSize: 11 }}>Sin registros para el mes actual.</div>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Prima legal */}
      <PrimaLegalPanel empleados={empleados} refreshKey={reloadKey} />

      {/* Beneficios y cesantías por empleado */}
      {empleados.map(emp => (
        <div key={emp.id}>
          <BeneficiosPanel empleadoId={emp.id} refreshKey={reloadKey} />
          <CesantiasPanel  empleadoId={emp.id} refreshKey={reloadKey} />
        </div>
      ))}

      {/* Modal empleado */}
      {showEmpForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 440 }}>
            <div style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontSize: 13, fontWeight: 700, color: '#08293f', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {editEmpId ? 'Editar empleado' : 'Nuevo empleado'}
                  </h2>
                  <div style={{ width: 28, height: 2, background: '#00b8e3', marginTop: 5 }} />
                </div>
                <button onClick={() => setShowEmpForm(false)} style={{ fontSize: 20, color: '#b3b3b3', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
              </div>
              <form onSubmit={guardarEmp}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label className="label">Cédula *</label>
                    <input className="input" value={empForm.cedula} style={inputStyle} required
                      onChange={e => setEmpForm(f => ({ ...f, cedula: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">Fecha ingreso</label>
                    <input className="input" type="date" value={empForm.fecha_ingreso} style={inputStyle}
                      onChange={e => setEmpForm(f => ({ ...f, fecha_ingreso: e.target.value }))} />
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label className="label">Nombre completo *</label>
                    <input className="input" value={empForm.nombre} style={inputStyle} required
                      onChange={e => setEmpForm(f => ({ ...f, nombre: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">Salario base</label>
                    <input className="input" type="number" value={empForm.salario_base} style={inputStyle} required min="0"
                      onChange={e => setEmpForm(f => ({ ...f, salario_base: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">Aux transporte</label>
                    <input className="input" type="number" value={empForm.aux_transporte} style={inputStyle} min="0"
                      onChange={e => setEmpForm(f => ({ ...f, aux_transporte: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" className="btn-primary" disabled={loading} style={{ flex: 1, padding: 9 }}>
                    {loading ? 'Guardando...' : editEmpId ? 'Actualizar' : 'Guardar'}
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setShowEmpForm(false)}>Cancelar</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .nomina-kpi { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// PÁGINA PRINCIPAL
// ════════════════════════════════════════════════════════════════
export default function Gastos() {
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#08293f', margin: 0 }}>Gastos y Egresos</h1>
        <p style={{ fontSize: 11, color: '#5a7a8a', letterSpacing: '0.06em', marginTop: 4 }}>Gastos fijos, variables y nómina cargada</p>
      </div>
      <TabGastosMes />
    </div>
  );
}
